/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Input, message } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { useMutation, queryCache } from 'react-query';

import { ThemedStyleFn } from '~src/hooks/useStyles';
import { DbFolder } from '~src/types/db';
import { editFolder } from '~src/services/api';
import { appLog } from '~src/utils/log';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { useProjectId } from '~src/hooks/useProjectId';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';

export interface EditFolderModalProps extends Partial<ModalProps> {
  folder?: DbFolder;
}
export const EditFolderModal: React.FC<EditFolderModalProps> = ({
  folder,
  onCancel,
  ...modalProps
}) => {
  const intl = useIntl();
  const projectId = useProjectId();
  const [nameVal, setNameVal] = useState(folder?.name || '');
  const [descriptionVal, setDescriptionVal] = useState(
    folder?.description || ''
  );
  const nameInputRef = useRef<Input>(null);
  const foldersQuery = useFolders(
    {
      as_admin:
        window.sessionStorage.getItem('appState') === 'superUser'
          ? true
          : undefined,
    },
    { enabled: false }
  );
  const [doEditFolder, { isLoading }] = useMutation(editFolder, {
    onSuccess: (theFolder) => {
      // hide modal
      // @ts-ignore-line
      onCancel && onCancel();

      queryCache.invalidateQueries('folders');
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'folder-edit-failed' }));
      appLog('EditFolderModal -> editFolder -> item', item);
      appLog('EditFolderModal -> editFolder -> err', error);
    },
  });

  // update values based on selected folder
  useEffect(() => {
    setNameVal(folder?.name || '');
    setDescriptionVal(folder?.description || '');
  }, [folder]);

  useEffect(() => {
    if (modalProps.visible) {
      setTimeout(() => nameInputRef.current?.focus(), 350);

      // fetch folders if data is older than 1min
      if (Date.now() - foldersQuery.updatedAt > 60 * 1000) {
        foldersQuery.refetch();
      }
    }
  }, [modalProps.visible]);

  const isValid = () => {
    if (!folder || !nameVal) return false;

    // check if folder with the same name exists
    if (foldersQuery.data?.length) {
      const trimmedName = nameVal.trim();

      const exists = foldersQuery.data.some(
        (v) =>
          v.parent_id === folder.parent_id &&
          v.id !== folder.id &&
          v.name.trim() === trimmedName
      );

      if (exists) return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!folder || !isValid()) return;

    doEditFolder({
      projectId: String(projectId),
      folderData: {
        ...folder,
        name: nameVal,
        description: descriptionVal,
      },
    });
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'edit-folder' })}
          icon={['far', 'pen']}
        />
      }
      okText={intl.formatMessage({ id: 'save' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={420}
      centered
      okButtonProps={{ disabled: !isValid(), loading: isLoading }}
      onOk={handleSubmit}
      onCancel={onCancel}
      {...modalProps}>
      <Input
        ref={nameInputRef}
        placeholder={intl.formatMessage({ id: 'name' })}
        allowClear
        autoCapitalize="words"
        value={nameVal}
        onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
        onChange={({ target: { value } }) => setNameVal(value)}
      />
      <Input.TextArea
        css={{
          marginTop: 10,
        }}
        placeholder={intl.formatMessage({ id: 'description' })}
        value={descriptionVal}
        onChange={({ target: { value } }) => setDescriptionVal(value)}
      />
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({});
